/**
 * Der Satz je Einheit einer Leistung — **Preiskategorie mal Mengenstufe**.
 *
 * Luca, 18.09.2026: *„Ja, Verschmutzungsgrad ist eine unterschiedliche
 * Preiskategorie. Wenn ich sage: Die PV-Platten sind 1.200 m² bei starker
 * Verschmutzung, greift ein anderer Preis als bei leichter Verschmutzung."*
 *
 * ## Warum eine Kategorie und kein Faktor
 *
 * Ein Faktor rechnet: Grundpreis mal Zahl. Damit steht jede Kategorie im
 * selben Verhältnis zum Grundpreis, und wer den Grundpreis ändert, ändert alle
 * Grade mit. Eine Kategorie **rechnet nicht** — sie wird abgelesen. Drei Sätze
 * je m², jeder für sich verhandelt, keiner aus einem anderen abgeleitet.
 *
 * Das ist auch der Grund, warum hier nichts gerundet wird: `quantity_tier.
 * price_net` trägt vier Nachkommastellen, und der Satz kommt aus der Spalte
 * heraus, wie er hineingeschrieben wurde. Gerundet wird erst der Positionswert.
 *
 * ## Zwei Kriterien, eine Tabelle
 *
 * `quantity_tier (service_type_id, variant_id, from_quantity) → price_net` —
 * dieselbe Form, die `rate (machine_type_id, variant_id, from_day)` bei den
 * Maschinen seit Migration 0003 hat. Eine Staffel **je** Kategorie:
 *
 * ```
 *   leicht   ab      0   0,42
 *   leicht   ab  2.000   0,38
 *   stark    ab      0   0,61      <- kein Vielfaches von 0,42
 *   stark    ab  2.000   0,55
 * ```
 *
 * Wer nicht staffelt, hat je Kategorie eine Zeile ab `0`. Wer keine Kategorien
 * führt, hat Zeilen mit `variant_id = null`.
 *
 * ## Was ohne gepflegten Satz passiert: nichts
 *
 * Kein Rückfall auf den Stammsatz, wenn die Leistungsart Kategorien führt. Ein
 * Grad ohne eigenen Satz bekommt **keinen** Preis, und die Antwort sagt warum
 * (`fehlt`). Der Stammsatz an seiner Stelle wäre genau der Grundsatz, den die
 * Entscheidung vom 18.09.2026 ausgeschlossen hat — nur unsichtbar.
 *
 * ## Der Vorgabegrad — eine Annahme, keine Feststellung (24.09.2026, 0184)
 *
 * Lisa: Der Kunde gibt am Telefon nur die Fläche durch — ein Preis soll
 * trotzdem entstehen. Luca hat entschieden (W4, Weg c): **je Leistungsart ein
 * Vorgabegrad im Stamm** (`service_type.default_variant_id`). Leer heißt wie
 * bisher: ohne festgestellten Grad kein Satz. Gesetzt heißt: Es wird mit ihm
 * gerechnet, und die Antwort sagt ausdrücklich, dass er **angenommen** und
 * nicht festgestellt ist (`kategorie_angenommen`, Schritt
 * `preis.mengenkategorie_angenommen`). Ein festgestellter Grad schlägt die
 * Vorgabe immer. Welcher Grad Vorgabe ist, entscheidet der Betrieb — hier
 * steht keiner.
 *
 * ## Der abgelesene Satz ist der Einzelpreis (21.09.2026, Migration 0164)
 *
 * Bis zum 21.09.2026 lief hinter dieser Datei noch `faktorenAnwenden`:
 * `Grundpreis × Faktor + Aufschlag`. Die Gattung „Preistreiber" ist abgebaut —
 * Anlagenart ist eine **Preiskategorie** wie der Verschmutzungsgrad, die
 * Wasserbereitstellung ein **Leistungszuschlag** und der Turnus ein
 * **Rabatt**. Was hier herauskommt, ist damit der Satz je Einheit; danach
 * rechnet nichts mehr daran.
 */
import { geld } from "./typen.js";
function ohneSatz(grund, kategorie, schritte) {
    schritte.push({ key: 'preis.mengensatz_fehlt', grund });
    return { satz: null, satz_id: null, kategorie, kategorie_angenommen: false, schritte, fehlt: grund };
}
/**
 * Die Stufe, deren `from_quantity` die Menge noch erreicht.
 *
 * Unter der untersten Stufe gilt die unterste — wie `stufeFuerDauer` es bei
 * den Miettagen tut. Eine Staffel, die ab 500 beginnt, ist keine Aussage
 * darueber, dass 400 nichts kosten; sie ist eine unvollstaendig gepflegte
 * Staffel, und der unterste Satz ist die einzige Zahl, die der Betrieb dazu
 * hingeschrieben hat.
 */
function stufeFuerMenge(saetze, menge) {
    const sortiert = [...saetze].sort((a, b) => a.from_quantity - b.from_quantity);
    let treffer = sortiert[0] ?? null;
    for (const satz of sortiert) {
        if (menge >= satz.from_quantity)
            treffer = satz;
    }
    return treffer;
}
/**
 * Den Satz je Einheit finden — aus Kategorie und Menge, in dieser Reihenfolge.
 *
 * Die Kategorie waehlt die Zeilen aus, die Menge waehlt darin die Stufe. Nicht
 * umgekehrt: „1.200 m²" allein ist keine Preisaussage, solange nicht feststeht,
 * wie schmutzig die 1.200 m² sind.
 */
export function mengensatzFinden(frage) {
    const schritte = [];
    const fuehrtKategorien = frage.kategorien.length > 0;
    const festgestellt = frage.kategorie_id === null
        ? null
        : frage.kategorien.find((k) => k.id === frage.kategorie_id) ?? null;
    /* Die Vorgabe greift nur, wo NICHTS festgestellt ist -- nicht, wo ein
       unbekannter Grad steht: Der waere ein Fehler, und ihn still durch die
       Vorgabe zu ersetzen, hiesse ihn zu verstecken. */
    const vorgabe = frage.kategorie_id === null && (frage.vorgabe_kategorie_id ?? null) !== null
        ? frage.kategorien.find((k) => k.id === frage.vorgabe_kategorie_id) ?? null
        : null;
    /* **Gibt es genau eine Kategorie, ist sie die** (P8-05, 05.10.2026). Eine
       Wahl aus einer ist keine: Die Leistungsart, die nur einen Grad führt,
       stand ohne festgestellten Grad ohne Satz da („kategorie_fehlt") — und die
       Annahme fror 0,00 ein, während Liste und Position einen Preis zeigten.
       Das ist keine Annahme über den Zustand vor Ort, darum nicht
       „angenommen": Es gibt nichts anderes, das gemeint sein könnte. Ein
       unbekannter Grad bleibt ein Fehler (oben, `kategorie_id` gesetzt). */
    const einzige = frage.kategorie_id === null && vorgabe === null && frage.kategorien.length === 1
        ? frage.kategorien[0] ?? null
        : null;
    const kategorie = festgestellt ?? vorgabe ?? einzige;
    const angenommen = festgestellt === null && vorgabe !== null;
    /* Eine Leistungsart mit Graden ohne festgestellten Grad hat keinen Preis.
       Den Stammsatz zu nehmen hiesse, still den guenstigsten Fall anzunehmen --
       und das steht dann im Angebot, ohne dass jemand hingesehen hat. */
    if (fuehrtKategorien && kategorie === null) {
        return ohneSatz('kategorie_fehlt', null, schritte);
    }
    if (kategorie !== null) {
        schritte.push({
            key: angenommen ? 'preis.mengenkategorie_angenommen' : 'preis.mengenkategorie',
            code: kategorie.code,
            ...(kategorie.label === undefined ? {} : { label: kategorie.label }),
        });
    }
    const eigene = frage.saetze.filter((s) => s.variant_id === (kategorie?.id ?? null));
    if (eigene.length === 0) {
        /* Ohne Kategorien faellt es auf den Stammsatz zurueck -- so rechnet die
           Leistungsart, die nie eine Staffel bekommen hat, weiter wie bisher. Mit
           Kategorien gibt es diesen Rueckfall nicht (siehe Kopf der Datei). */
        if (fuehrtKategorien) {
            return { ...ohneSatz('kategorie_ohne_satz', kategorie, schritte), kategorie_angenommen: angenommen };
        }
        if (frage.grundpreis === null)
            return ohneSatz('kein_satz', null, schritte);
        schritte.push({ key: 'preis.mengensatz_stamm', satz: frage.grundpreis });
        return {
            satz: frage.grundpreis, satz_id: null, kategorie: null, kategorie_angenommen: false,
            schritte, fehlt: null,
        };
    }
    const stufe = stufeFuerMenge(eigene, frage.menge);
    if (stufe === null) {
        return { ...ohneSatz('kein_satz', kategorie, schritte), kategorie_angenommen: angenommen };
    }
    schritte.push({
        key: 'preis.mengenstaffel',
        ab_menge: stufe.from_quantity,
        satz: stufe.price_net,
        menge: frage.menge,
        // Ob die Menge ueber der Stufe liegt oder unter der untersten: Wer den
        // Beleg liest, soll nicht raten muessen, warum 400 denselben Satz haben
        // wie 500.
        unter_erster_stufe: frage.menge < stufe.from_quantity,
        stufen: eigene.length,
    });
    return {
        satz: stufe.price_net,
        satz_id: stufe.id,
        kategorie,
        kategorie_angenommen: angenommen,
        schritte,
        fehlt: null,
    };
}
/**
 * **Die Mindestmenge hebt an** (Entscheidung E10, 04.10.2026) — die eine
 * Regel fuer jede Menge, die an einer Position steht: beim Anlegen, beim
 * Aendern, beim Zurueckschreiben aus der Ortsaufnahme und **seit dem
 * 08.10.2026 in der Merkliste** (`mengenpostenRechnen`). Bis dahin stand sie
 * nur in der API (`dienste/mindestmenge.ts`), und die Merkliste haette eine
 * zweite gebraucht.
 *
 * **Menge 0 bleibt 0.** An einer Leistung mit Ortsaufnahme-Pflicht heisst 0
 * „Menge folgt aus dem Aufmass" — eine angehobene Zahl saehe dort aus wie
 * gemessen. Wenn das Aufmass kommt, hebt es an.
 */
export function mengeAngehoben(menge, mindestmenge) {
    if (mindestmenge === null || mindestmenge === undefined || !(mindestmenge > 0))
        return menge;
    if (!Number.isFinite(menge) || !(menge > 0))
        return menge;
    return Math.max(menge, mindestmenge);
}
/**
 * **Menge × Satz einer Leistung, bevor es eine Position gibt** (08.10.2026).
 *
 * Luca: *„Ich will bei der PV-Schnellvermietung … auch direkt die
 * Quadratmeterzahl eintragen können, sodass ich das auch direkt vermieten
 * kann."* Die Merkliste rechnet damit, was „Jetzt vermieten" danach an die
 * Position schreibt und die Annahme einfriert — **mit denselben zwei
 * Funktionen in derselben Reihenfolge** wie die API (`POST …/positionen`):
 * erst die Mindestmenge (`mengeAngehoben`), dann mit dieser Menge die Stufe
 * (`mengensatzFinden`). Eine zweite Rechnung in der Maske liefe irgendwann
 * auseinander.
 *
 * Gerechnet wird der **Plan**. Abgerechnet wird weiterhin nur das erfasste
 * Ist (Regel 3, `rechnungszeilen`): Die Planmenge belegt dort nur das
 * Eintragen vor (Rechenreferenz 4 und 10z″).
 */
export function mengenpostenRechnen(frage) {
    const genannt = Number.isFinite(frage.menge) ? Math.max(0, frage.menge) : 0;
    const menge = mengeAngehoben(genannt, frage.mindestmenge);
    const antwort = mengensatzFinden({ ...frage, menge });
    return {
        menge,
        angehoben: menge > genannt,
        satz: antwort.satz,
        betrag: antwort.satz === null ? null : geld(menge * antwort.satz),
        antwort,
    };
}
//# sourceMappingURL=mengenpreis.js.map